import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import { Button } from "../components/ui/button";
import { 
  Receipt, Loader2, Clock, CheckCircle2, AlertCircle, 
  FileText, DollarSign, Calendar, ChevronRight, Download,
  Shield, User, Building, CreditCard, Plus
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Initialize Stripe with the correct environment variable for Create React App
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const EmbeddedLayout = ({ children }) => <>{children}</>;

const FinancePageHeader = ({ title, detail, aside }) => <EditorialPageHeader
  eyebrow="Finance"
  title={title}
  description={detail}
  headingId="invoices-heading"
  aside={aside}
  className="mb-6"
/>;

// Card Element Options
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1e293b',
      '::placeholder': {
        color: '#94a3b8',
      },
    },
    invalid: {
      color: '#ef4444',
    },
  },
};

// Payment Form Component
const PaymentForm = ({ invoice, paymentMethods, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { token } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [useNewCard, setUseNewCard] = useState(paymentMethods.length === 0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentMethods.length > 0 ? paymentMethods[0].id : null
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe) {
      toast.error("Stripe not initialized");
      return;
    }

    setProcessing(true);
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (useNewCard) {
        // Get client secret for new card payment
        const response = await axios.post(
          `${API}/invoices/${invoice.invoice_id}/pay-with-stripe`,
          {},
          { headers }
        );

        if (response.data.client_secret) {
          const cardElement = elements.getElement(CardElement);
          const { error, paymentIntent } = await stripe.confirmCardPayment(
            response.data.client_secret,
            {
              payment_method: {
                card: cardElement,
              }
            }
          );

          if (error) {
            toast.error(error.message);
            setProcessing(false);
            return;
          }

          if (paymentIntent.status === 'succeeded') {
            // Confirm payment on backend
            await axios.post(
              `${API}/invoices/${invoice.invoice_id}/confirm-payment`,
              { payment_intent_id: paymentIntent.id },
              { headers }
            );
            toast.success("Payment successful!");
            onSuccess();
          }
        }
      } else {
        // Use saved payment method
        const response = await axios.post(
          `${API}/invoices/${invoice.invoice_id}/pay-with-stripe`,
          { payment_method_id: selectedPaymentMethod },
          { headers }
        );

        if (response.data.status === 'paid') {
          toast.success("Payment successful!");
          onSuccess();
        } else {
          toast.error("Payment failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {paymentMethods.length > 0 && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-slate-700">Payment Method</label>
          
          {/* Saved Cards */}
          {paymentMethods.map((pm) => (
            <div
              key={pm.id}
              onClick={() => {
                setSelectedPaymentMethod(pm.id);
                setUseNewCard(false);
              }}
              className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                !useNewCard && selectedPaymentMethod === pm.id
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <CreditCard className="w-5 h-5 text-slate-600" />
              <div className="flex-1">
                <p className="font-medium text-slate-900 capitalize">{pm.card.brand} •••• {pm.card.last4}</p>
                <p className="text-xs text-slate-500">Expires {pm.card.exp_month}/{pm.card.exp_year}</p>
              </div>
              {!useNewCard && selectedPaymentMethod === pm.id && (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              )}
            </div>
          ))}
          
          {/* New Card Option */}
          <div
            onClick={() => setUseNewCard(true)}
            className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
              useNewCard
                ? 'border-slate-900 bg-slate-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <Plus className="w-5 h-5 text-slate-600" />
            <span className="font-medium text-slate-900">Use a new card</span>
            {useNewCard && (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" />
            )}
          </div>
        </div>
      )}

      {/* Card Element (shown when using new card) */}
      {(useNewCard || paymentMethods.length === 0) && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Card Details</label>
          <div className="border border-slate-200 rounded-xl p-4">
            <CardElement options={cardElementOptions} />
          </div>
        </div>
      )}

      {/* Amount Summary */}
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">Amount Due</span>
          <span className="font-bold text-slate-900 text-lg">${invoice.total?.toFixed(2)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 rounded-xl"
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
          disabled={processing || !stripe}
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <DollarSign className="w-4 h-4 mr-2" />
              Pay ${invoice.total?.toFixed(2)}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

const AdminInvoices = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filter, setFilter] = useState("all");
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  
  // Super admin - create invoice
  const [createInvoiceDialog, setCreateInvoiceDialog] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    org_id: "",
    staff_name: "",
    staff_email: "",
    amount: 35.00,
    notes: ""
  });
  
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchInvoices();
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [token]);

  const fetchOrganizations = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/organizations`, { headers });
      setOrganizations(res.data || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.org_id || !invoiceForm.staff_name || !invoiceForm.staff_email) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setCreatingInvoice(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/invoices/background-check`, invoiceForm, { headers });
      toast.success("Background check invoice created and sent to organization");
      setCreateInvoiceDialog(false);
      setInvoiceForm({ org_id: "", staff_name: "", staff_email: "", amount: 35.00, notes: "" });
      fetchInvoices();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/invoices`, { headers });
      setInvoices(res.data || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setLoadError(error.response?.data?.detail || "We couldn't load your invoices. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'overdue':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-slate-50 text-slate-500 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return true;
    return inv.status === filter;
  });

  const totalPending = invoices
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + (inv.total || 0), 0);

  const totalPaid = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + (inv.total || 0), 0);

  if (loading) {
    return (
      <PageLayout>
        {embedded && <FinancePageHeader title="Invoices" detail="Review organization invoices, balances, and payment status." />}
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout title="Invoices">
        {embedded && <FinancePageHeader title="Invoices" detail="Review organization invoices, balances, and payment status." />}
        <section className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6" role="alert">
          <h2 className="text-lg font-semibold text-rose-950">Invoices unavailable</h2>
          <p className="mt-2 text-sm leading-6 text-rose-800">{loadError}</p>
          <Button onClick={fetchInvoices} className="mt-4 rounded-xl bg-rose-700 hover:bg-rose-800">Try again</Button>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6" data-testid="admin-invoices">
        {/* Header */}
        <FinancePageHeader
          title={isSuperAdmin ? "All Organization Invoices" : "Invoices from Sporthouse"}
          detail={isSuperAdmin ? "Create and manage invoices for organizations" : "View and manage invoices for platform services"}
          aside={isSuperAdmin ? (
            <Button
              onClick={() => setCreateInvoiceDialog(true)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
            >
              <Shield className="w-4 h-4 mr-2" />
              Create Background Check Invoice
            </Button>
          ) : null}
        />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${totalPending.toFixed(2)}</p>
                <p className="text-sm text-slate-500">Outstanding Balance</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${totalPaid.toFixed(2)}</p>
                <p className="text-sm text-slate-500">Total Paid</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Receipt className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{invoices.length}</p>
                <p className="text-sm text-slate-500">Total Invoices</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 bg-slate-100 p-1 rounded-xl w-fit">
          {[
            { value: 'all', label: 'All' },
            { value: 'pending', label: 'Pending' },
            { value: 'paid', label: 'Paid' },
            { value: 'overdue', label: 'Overdue' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === tab.value
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
              {tab.value === 'pending' && invoices.filter(i => i.status === 'pending').length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                  {invoices.filter(i => i.status === 'pending').length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Invoices List */}
        {filteredInvoices.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">No Invoices</h3>
            <p className="text-slate-500">
              {filter === 'all' 
                ? "You don't have any invoices yet"
                : `No ${filter} invoices found`}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="divide-y divide-slate-100">
              {filteredInvoices.map((invoice) => (
                <div 
                  key={invoice.invoice_id}
                  className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedInvoice(selectedInvoice?.invoice_id === invoice.invoice_id ? null : invoice)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      {/* Icon based on type */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        invoice.related_to?.type === 'background_check' 
                          ? 'bg-blue-50' 
                          : 'bg-slate-100'
                      }`}>
                        {invoice.related_to?.type === 'background_check' ? (
                          <Shield className="w-6 h-6 text-blue-600" />
                        ) : (
                          <Receipt className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{invoice.description}</p>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(invoice.status)}`}>
                            {getStatusIcon(invoice.status)}
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Invoice #{invoice.invoice_number}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(invoice.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </span>
                          {invoice.due_date && invoice.status === 'pending' && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              Due: {new Date(invoice.due_date).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric'
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xl font-bold text-slate-900">${invoice.total?.toFixed(2)}</p>
                      <ChevronRight className={`w-5 h-5 text-slate-400 mt-2 transition-transform ${
                        selectedInvoice?.invoice_id === invoice.invoice_id ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {selectedInvoice?.invoice_id === invoice.invoice_id && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      {/* Line Items */}
                      <div className="bg-slate-50 rounded-xl p-4 mb-4">
                        <h4 className="text-sm font-medium text-slate-700 mb-3">Line Items</h4>
                        <div className="space-y-2">
                          {invoice.line_items?.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-600">{item.description}</span>
                                {item.quantity > 1 && (
                                  <span className="text-slate-400">x{item.quantity}</span>
                                )}
                              </div>
                              <span className="font-medium text-slate-900">${item.total?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between text-sm font-semibold">
                          <span className="text-slate-700">Total</span>
                          <span className="text-slate-900">${invoice.total?.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* Related Info */}
                      {invoice.related_to && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                          {invoice.related_to.type === 'background_check' && (
                            <>
                              <User className="w-4 h-4" />
                              <span>Staff: {invoice.related_to.staff_name} ({invoice.related_to.staff_email})</span>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Notes */}
                      {invoice.notes && (
                        <p className="text-sm text-slate-500 mb-4">{invoice.notes}</p>
                      )}
                      
                      {/* Actions */}
                      {invoice.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button 
                            className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
                            onClick={async (e) => {
                              e.stopPropagation();
                              setPayingInvoice(invoice);
                              setLoadingPaymentMethods(true);
                              
                              try {
                                const headers = { Authorization: `Bearer ${token}` };
                                const res = await axios.get(
                                  `${API}/invoices/${invoice.invoice_id}/payment-methods`,
                                  { headers }
                                );
                                setPaymentMethods(res.data.payment_methods || []);
                              } catch (error) {
                                console.error("Error fetching payment methods:", error);
                                setPaymentMethods([]);
                              } finally {
                                setLoadingPaymentMethods(false);
                                setPaymentDialog(true);
                              }
                            }}
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay Now
                          </Button>
                          <Button 
                            variant="outline" 
                            className="rounded-xl"
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.info("Download feature coming soon");
                            }}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                        </div>
                      )}
                      
                      {invoice.status === 'paid' && invoice.paid_at && (
                        <div className="flex items-center gap-2 text-sm text-emerald-600">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>
                            Paid on {new Date(invoice.paid_at).toLocaleDateString('en-US', {
                              month: 'long', day: 'numeric', year: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900">Need Help?</h3>
              <p className="text-sm text-blue-700 mt-1">
                For billing questions or payment assistance, contact Sporthouse at{' '}
                <a href="mailto:billing@sporthouse.ai" className="underline font-medium">
                  billing@sporthouse.ai
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pay Invoice</DialogTitle>
            <DialogDescription>
              {payingInvoice && (
                <>Invoice #{payingInvoice.invoice_number} - {payingInvoice.description}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {loadingPaymentMethods ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : payingInvoice ? (
            <Elements stripe={stripePromise}>
              <PaymentForm
                invoice={payingInvoice}
                paymentMethods={paymentMethods}
                onSuccess={() => {
                  setPaymentDialog(false);
                  setPayingInvoice(null);
                  fetchInvoices();
                }}
                onCancel={() => {
                  setPaymentDialog(false);
                  setPayingInvoice(null);
                }}
              />
            </Elements>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog (Super Admin) */}
      <Dialog open={createInvoiceDialog} onOpenChange={setCreateInvoiceDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Background Check Invoice</DialogTitle>
            <DialogDescription>
              Create an invoice for a background check. The organization will be notified and can pay via Stripe.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Organization Select */}
            <div>
              <label className="text-sm font-medium text-slate-700">Organization *</label>
              <select
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                value={invoiceForm.org_id}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, org_id: e.target.value }))}
              >
                <option value="">Select organization...</option>
                {organizations.map(org => (
                  <option key={org.org_id} value={org.org_id}>{org.name}</option>
                ))}
              </select>
            </div>
            
            {/* Staff Name */}
            <div>
              <label className="text-sm font-medium text-slate-700">Staff Member Name *</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="John Smith"
                value={invoiceForm.staff_name}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, staff_name: e.target.value }))}
              />
            </div>
            
            {/* Staff Email */}
            <div>
              <label className="text-sm font-medium text-slate-700">Staff Member Email *</label>
              <input
                type="email"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="john@example.com"
                value={invoiceForm.staff_email}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, staff_email: e.target.value }))}
              />
            </div>
            
            {/* Amount */}
            <div>
              <label className="text-sm font-medium text-slate-700">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                value={invoiceForm.amount}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              />
              <p className="text-xs text-slate-500 mt-1">Default: $35.00 for standard background check</p>
            </div>
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notes (Optional)</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                rows={2}
                placeholder="Additional notes..."
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateInvoiceDialog(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateInvoice}
              disabled={creatingInvoice}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
            >
              {creatingInvoice ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Create Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminInvoices;
